// Task screen (the `task` step). Mounts jsPsych and runs a task built from the
// JOLIVE task definition via the pure translator. This is the keystone of the
// task port: it proves the engine end to end on one paradigm (flanker).
//
// Ship 2 scope: the definition is read inline from step.settings.task_definition
// (this is what the preview harness supplies, and the legacy inline path). The
// live backend delivery for custom/default tasks is the next ship.
//
// Phases handled here: fixation, stimulus (text), iti/blank, feedback. Each phase
// becomes one jsPsych trial; per-trial responses are captured and, on finish,
// mapped to the parity-compatible result rows by the translator, then submitted.

import { useEffect, useRef, useState } from "react";
import { initJsPsych } from "jspsych";
import htmlKeyboardResponse from "@jspsych/plugin-html-keyboard-response";
import "jspsych/css/jspsych.css";
import type { WebRendererProps } from "./registry";
import type { DocMidBlockSurvey, VideoSurveyResponse } from "../api/types";
import { fetchTask } from "../session/sessionDriver";
import { runMrt } from "./task/mrt";
import { SurveyForm, type SurveyResponse } from "./survey/SurveyForm";
import {
  flattenTask,
  computeTrialResult,
  hasResponsePhase,
  anchorOnset,
  joliveKeyToJsPsych,
  jsPsychKeyToJolive,
  pickFontSize,
  type TaskDefinition,
  type Phase,
  type TrialPlan,
  type Stim,
  type Captured,
} from "./task/translator";

function resolveLabel(value: string, keys: Record<string, string>): string {
  return value in keys ? keys[value] : value;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Style values are interpolated into style="..." strings, where escapeHtml is not
// enough (a `"` in a color breaks out of the attribute). Allowlist real CSS colors
// (hex / named / rgb[a]/hsl[a]) and coerce sizes to finite numbers; anything else
// falls back. Text content is still escaped separately via escapeHtml.
export function safeColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  if (/^[a-zA-Z]+$/.test(v)) return v;
  if (/^(rgb|rgba|hsl|hsla)\([0-9.,%\s/]+\)$/i.test(v)) return v;
  return fallback;
}
export function safeNum(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function fixationHtml(phase: Phase): string {
  const size = safeNum(phase.size, 48);
  const color = safeColor(phase.color, "#FFFFFF");
  return `<div style="font-family:Arial,sans-serif;font-size:${size}px;color:${color};font-weight:700;line-height:1">+</div>`;
}

function stimulusHtml(stim: Stim, size: number): string {
  const text = escapeHtml(String(stim.text ?? ""));
  const color = safeColor(stim.color, "#FFFFFF");
  return `<div style="font-family:Arial,sans-serif;color:${color};font-size:${safeNum(size, 72)}px;font-weight:700;white-space:pre">${text}</div>`;
}

function feedbackHtml(correct: boolean, settings: Record<string, unknown>): string {
  const correctText = (settings.fb_correct_text as string) ?? "Correct";
  const incorrectText = (settings.fb_incorrect_text as string) ?? "Incorrect";
  const correctColor = safeColor(settings.fb_correct_color, "#4ade80");
  const incorrectColor = safeColor(settings.fb_incorrect_color, "#f87171");
  const text = correct ? correctText : incorrectText;
  const color = correct ? correctColor : incorrectColor;
  return `<div style="font-family:Arial,sans-serif;color:${color};font-size:40px;font-weight:700">${escapeHtml(text)}</div>`;
}

// Matches the desktop InstructionScreen: Arial, white body, grey "Press SPACE
// to continue" prompt. Advance is space-only (see the instruction trials below).
function instructionHtml(text: string): string {
  return (
    `<div style="font-family:Arial,sans-serif;max-width:900px;color:#FFFFFF;font-size:26px;line-height:1.5;white-space:pre-line;text-align:center">` +
    `${escapeHtml(text)}` +
    `<div style="margin-top:40px;color:#AAAAAA;font-size:16px">Press SPACE to continue</div></div>`
  );
}

// Task-switching cue frame: a colored border (and/or top label) near the screen
// edges that signals which task applies, optionally with the digit centred on
// top (the stimulus phase reuses the frame). Mirrors the desktop CueDisplay.
function cueFrameHtml(stim: Stim, settings: Record<string, unknown>, centerText: string): string {
  const cueColor = safeColor(stim.cue_color, "#FFFFFF");
  const cueType = (settings.cue_type as string) ?? "border";
  const borderWidth = safeNum(settings.cue_border_width, 6);
  const cueText = (stim.cue_text as string) ?? "";
  const size = safeNum(stim.font_size, 72);
  const centerColor = safeColor(stim.color, "#FFFFFF");
  let html = "";
  if (cueType === "border" || cueType === "both") {
    html += `<div style="position:fixed;top:40px;left:40px;right:40px;bottom:40px;border:${borderWidth}px solid ${cueColor};box-sizing:border-box;pointer-events:none"></div>`;
  }
  if (cueType === "text" || cueType === "both") {
    html += `<div style="position:fixed;top:64px;left:0;right:0;text-align:center;color:${cueColor};font-family:Arial,sans-serif;font-size:20px;font-weight:700;letter-spacing:1px">${escapeHtml(cueText.toUpperCase())}</div>`;
  }
  if (centerText) {
    html += `<div style="font-family:Arial,sans-serif;color:${centerColor};font-size:${size}px;font-weight:700">${escapeHtml(centerText)}</div>`;
  }
  return html || "<div></div>";
}

// PVT reaction-time feedback, color-coded: green if under the lapse threshold,
// red if a lapse, amber for no response. Mirrors the desktop RtFeedbackDisplay.
function rtFeedbackHtml(rt: number | undefined, settings: Record<string, unknown>): string {
  const normal = safeColor(settings.rt_normal_color, "#00CC00");
  const lapse = safeColor(settings.rt_lapse_color, "#FF4444");
  const timeout = safeColor(settings.rt_timeout_color, "#FFAA00");
  const threshold = (settings.lapse_threshold_ms as number) ?? 500;
  let text: string;
  let color: string;
  if (rt == null) {
    text = "No response";
    color = timeout;
  } else {
    text = `${Math.round(rt)} ms`;
    color = rt >= threshold ? lapse : normal;
  }
  return `<div style="font-family:Arial,sans-serif;color:${color};font-size:36px;font-weight:700">${text}</div>`;
}

// PVT running millisecond counter (animated in on_load) and the static dot mode.
function counterHtml(stim: Stim, settings: Record<string, unknown>): string {
  const color = safeColor(stim.color ?? settings.stimulus_color, "#FF0000");
  const size = safeNum(stim.font_size, 72);
  return `<div style="font-family:Arial,sans-serif;color:${color};font-size:${size}px;font-weight:700"><span id="jol-pvt-counter">0</span></div>`;
}

function dotHtml(stim: Stim, settings: Record<string, unknown>): string {
  const color = safeColor(stim.color ?? settings.stimulus_color, "#FF0000");
  const size = safeNum(stim.font_size, 72);
  return `<div style="font-family:Arial,sans-serif;color:${color};font-size:${size}px;font-weight:700">\u25CF</div>`;
}

export default function TaskRenderer({ step, session, onDone }: WebRendererProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const ranRef = useRef(false);

  // Mid-block survey state (P4). Adding React state is safe: the run effect is
  // ranRef-guarded with empty deps, so a re-render never restarts the task.
  const [activeSurvey, setActiveSurvey] = useState<DocMidBlockSurvey | null>(null);
  const activeSurveyRef = useRef<DocMidBlockSurvey | null>(null);
  const surveyResponsesRef = useRef<VideoSurveyResponse[]>([]);
  const jsPsychRef = useRef<ReturnType<typeof initJsPsych> | null>(null);
  // MRT resolves a survey via a promise (imperative, not jsPsych); the overlay
  // submit calls this to hand responses back and resume the block.
  const mrtSurveyResolveRef = useRef<((responses: SurveyResponse[]) => void) | null>(null);

  function fireSurvey(trigger: DocMidBlockSurvey) {
    activeSurveyRef.current = trigger;
    setActiveSurvey(trigger);
  }

  // The overlay's Submit: bundle the responses, clear the overlay, and resume
  // whichever engine paused (jsPsych timeline or the MRT block loop).
  function onTaskSurveySubmit(responses: SurveyResponse[]) {
    const trigger = activeSurveyRef.current;
    if (trigger) {
      surveyResponsesRef.current.push({
        survey_id: trigger.survey_id,
        survey_name: trigger.survey_name,
        responses,
      });
    }
    activeSurveyRef.current = null;
    setActiveSurvey(null);
    const mrtResolve = mrtSurveyResolveRef.current;
    if (mrtResolve) {
      mrtSurveyResolveRef.current = null;
      mrtResolve(responses);
    } else {
      jsPsychRef.current?.resumeExperiment();
    }
  }

  useEffect(() => {
    if (ranRef.current || !hostRef.current) return;
    ranRef.current = true;
    const host = hostRef.current;
    let jsPsychInstance: ReturnType<typeof initJsPsych> | null = null;
    let mrtCleanup: (() => void) | null = null;
    let cancelled = false;
    // React StrictMode (dev) runs setup -> cleanup -> setup synchronously on
    // mount. Because the preview path has no await, our setup (including the
    // MRT key listener) runs fully inside the first setup, so the immediate
    // cleanup would tear it down before anything could happen. We arm teardown
    // on a later tick so that synchronous, spurious cleanup is ignored and only
    // a real unmount (a later commit) tears down.
    let armed = false;
    setTimeout(() => {
      armed = true;
    }, 0);

    host.innerHTML =
      '<div style="font-family:Arial,sans-serif;color:#AAAAAA;font-size:18px">Loading task...</div>';

    (async () => {
      const settings = (step.settings ?? {}) as Record<string, unknown>;
      let def = settings.task_definition as TaskDefinition | undefined;
      // Mid-block surveys ("every N trials"). Live sessions get them resolved
      // (with inline questions) from the task endpoint; the inline/preview path
      // reads them off settings (unresolved -> no questions -> won't fire).
      let midBlockSurveys: DocMidBlockSurvey[] = Array.isArray(settings.mid_block_surveys)
        ? (settings.mid_block_surveys as DocMidBlockSurvey[])
        : [];

      // Live sessions deliver the definition via the scoped task endpoint when
      // it is not inlined on the step (the preview supplies it inline).
      if (!def) {
        try {
          const content = await fetchTask(session);
          def = content.task_definition as unknown as TaskDefinition;
          midBlockSurveys = content.mid_block_surveys ?? [];
        } catch {
          def = undefined;
        }
      }
      if (cancelled) return;

      // The single count trigger to honor (matching desktop's one-survey model).
      // Require inline questions so an unresolved preview trigger never fires an
      // empty form.
      const surveyTrigger = midBlockSurveys.find(
        (t) =>
          (t.trigger_kind ?? "count") === "count" &&
          t.value > 0 &&
          (t.questions?.length ?? 0) > 0,
      );
      const surveyN = surveyTrigger ? surveyTrigger.value : 0;

      if (!def || !def.trial_template || !def.blocks) {
        host.innerHTML =
          '<div style="font-family:Arial,sans-serif;color:#fff;font-size:18px">This task could not be loaded.</div>';
        return;
      }

      const definition = def;
      const respKeys = definition.response_keys ?? {};
      const taskSettings = definition.settings ?? {};
      const bg = (taskSettings.background_color as string) ?? "#000000";
      host.style.background = bg;
      host.innerHTML = "";

      // MRT runs on its own drift-free beat engine (a chained-timer timeline
      // would accumulate rhythm drift over many beats), so it bypasses jsPsych.
      if (definition.task_type === "mrt") {
        // Surveys snap to BLOCK boundaries for MRT (pausing mid-rhythm would
        // break the metronome). onBlockBoundary fires the overlay when a new
        // "every N trials" multiple has been crossed, and awaits the answer.
        const mrtFired = { count: 0 };
        const onBlockBoundary = async (nonPracticeTrials: number) => {
          if (!surveyTrigger || surveyN <= 0) return;
          const due = Math.floor(nonPracticeTrials / surveyN);
          if (due > mrtFired.count) {
            mrtFired.count = due;
            await new Promise<SurveyResponse[]>((resolve) => {
              mrtSurveyResolveRef.current = resolve;
              fireSurvey(surveyTrigger);
            });
            // onTaskSurveySubmit already bundled the response.
          }
        };
        mrtCleanup = runMrt(
          host,
          definition,
          respKeys,
          (result) => {
            if (!cancelled) {
              onDone({
                kind: "submit",
                result: { ...result, survey_responses: surveyResponsesRef.current },
              });
            }
          },
          onBlockBoundary,
        );
        return;
      }

      const blocks = flattenTask(definition);
      const allPlans = blocks.flatMap((b) => b.trials);
      const trialHasResp = hasResponsePhase(definition);
      const captured: Record<number, Captured> = {};

      const jsPsych = initJsPsych({
        display_element: host,
        on_finish: () => {
          const trials = allPlans.map((p) =>
            computeTrialResult(p, captured[p.trialNumber], respKeys, trialHasResp),
          );
          const keyAssignment =
            Object.entries(respKeys)
              .map(([k, v]) => `${k}:${v}`)
              .join(",") || null;
          onDone({
            kind: "submit",
            result: {
              task_name: definition.task_name ?? "",
              task_type: definition.task_type ?? "",
              key_assignment: keyAssignment,
              trials,
              end_trigger: "complete",
              survey_responses: surveyResponsesRef.current,
            },
          });
        },
      });
      jsPsychInstance = jsPsych;
      jsPsychRef.current = jsPsych;

      const timeline: Record<string, unknown>[] = [];

      if (definition.instructions) {
        timeline.push({
          type: htmlKeyboardResponse,
          stimulus: instructionHtml(definition.instructions),
          choices: [" "], // space advances, matching the desktop
        });
      }

      const jsChoices = Object.keys(respKeys).map(joliveKeyToJsPsych);
      // RT is measured from stimulus onset and may be captured across phases
      // (e.g. n-back accepts a response during the stimulus AND the gap that
      // follows it), so onset is set once per trial at the stimulus and reused.
      // trialNumber tracks which trial owns the current onset so a new trial never
      // inherits the previous trial's onset (see anchorOnset). 0 = "none yet"
      // (trialNumber is 1-based, so the first phase of trial 1 starts unset).
      const timing = { onset: 0, phaseStart: 0, trialNumber: 0 };

      const phaseTrial = (phase: Phase, plan: TrialPlan): Record<string, unknown> | null => {
        const stim = plan.stimulus;
        const isAwait = phase.await_response === true;

        // Feedback is never a response phase; render it only when enabled. Its
        // duration is the block override, else the phase duration, else 500ms.
        if (phase.type === "feedback") {
          if (!plan.showFeedback) return null;
          const fbDuration = plan.feedbackDurationMs ?? phase.duration_ms ?? 500;
          return {
            type: htmlKeyboardResponse,
            stimulus: () => {
              const cap = captured[plan.trialNumber];
              const correctLabel = resolveLabel(plan.correctResponse, respKeys);
              const actual = cap ? respKeys[cap.key] ?? "" : null;
              const correct =
                correctLabel === "no_response" || correctLabel === ""
                  ? !cap
                  : Boolean(cap) && actual === correctLabel;
              return feedbackHtml(correct, taskSettings);
            },
            choices: "NO_KEYS",
            trial_duration: fbDuration,
          };
        }

        // PVT reaction-time feedback (always shown, not gated by show_feedback).
        if (phase.type === "rt_feedback") {
          return {
            type: htmlKeyboardResponse,
            stimulus: () => rtFeedbackHtml(captured[plan.trialNumber]?.rt, taskSettings),
            choices: "NO_KEYS",
            trial_duration: phase.duration_ms ?? 1000,
          };
        }

        const isStimulus = phase.type === "stimulus";
        const stimType = (stim.type as string) ?? "text";
        const isCounter = isStimulus && stimType === "counter";

        // Visual content for the phase.
        let html: string;
        if (phase.type === "fixation") {
          html = fixationHtml(phase);
        } else if (phase.type === "cue") {
          html = cueFrameHtml(stim, taskSettings, ""); // cue alone, no digit
        } else if (isStimulus) {
          if (isCounter) {
            html = counterHtml(stim, taskSettings);
          } else if (stimType === "target") {
            html = dotHtml(stim, taskSettings);
          } else if (stim.cue_color) {
            html = cueFrameHtml(stim, taskSettings, String(stim.text ?? "")); // cue frame + digit
          } else {
            html = stimulusHtml(stim, pickFontSize(stim));
          }
        } else {
          html = ""; // iti / blank / unknown render nothing on the background
        }

        // Timing rules mirror the desktop runner exactly:
        //  - a phase with duration_ms runs its full duration; a response is
        //    captured but does NOT end the phase early (n-back stimulus + gap)
        //  - an await phase with only timeout_ms ends as soon as a response
        //    arrives, or at the timeout (flanker, stroop, ldt, sart)
        //  - an await phase with neither waits indefinitely for a response
        let trialDuration: number | null;
        let responseEnds: boolean;
        if (phase.duration_ms != null) {
          trialDuration = phase.duration_ms;
          responseEnds = false;
        } else if (isAwait && phase.timeout_ms != null) {
          trialDuration = phase.timeout_ms;
          responseEnds = true;
        } else if (isAwait) {
          trialDuration = null;
          responseEnds = true;
        } else {
          const perStim = stim.iti_duration_ms as number | undefined;
          trialDuration = perStim ?? 0;
          responseEnds = false;
        }

        const trial: Record<string, unknown> = {
          type: htmlKeyboardResponse,
          stimulus: html,
          choices: isAwait ? jsChoices : "NO_KEYS",
          trial_duration: trialDuration,
          response_ends_trial: responseEnds,
          on_load: () => {
            const now = performance.now();
            // The RT zero-point stays the stimulus onset; this only adds a
            // per-trial reset so a malformed template can't inherit a prior
            // trial's onset. Behavior-identical for all 8 shipped tasks.
            timing.onset = anchorOnset({
              isStimulus,
              isAwait,
              now,
              onset: timing.onset,
              trialNumber: plan.trialNumber,
              lastTrialNumber: timing.trialNumber,
            });
            timing.trialNumber = plan.trialNumber;
            timing.phaseStart = now;
            // The PVT counter ticks up from onset; the loop stops itself once
            // the trial ends and its element is removed from the DOM.
            if (isCounter) {
              const tick = () => {
                const node = document.getElementById("jol-pvt-counter");
                if (!node) return;
                node.textContent = String(Math.max(0, Math.round(performance.now() - timing.onset)));
                requestAnimationFrame(tick);
              };
              requestAnimationFrame(tick);
            }
          },
        };

        if (isAwait) {
          trial.on_finish = (data: { response: string | null; rt: number | null }) => {
            if (data.response == null || data.rt == null) return;
            if (captured[plan.trialNumber]) return; // first response across phases wins
            const joliveKey = jsPsychKeyToJolive(data.response);
            if (!(joliveKey in respKeys)) return;
            const absolute = timing.phaseStart + data.rt; // response time, absolute
            captured[plan.trialNumber] = { key: joliveKey, rt: absolute - timing.onset };
          };
        }

        return trial;
      };

      // "Every N trials" counts NON-practice JOLIVE trials globally (matching
      // desktop). Each JOLIVE trial expands to several jsPsych phase-nodes, so we
      // fire on a due plan's LAST phase -- counting jsPsych nodes would fire N
      // times too often.
      let surveyTrialCount = 0;
      let prevWasPractice = false;
      for (const block of blocks) {
        const isPractice = (block.label ?? "").trim().toLowerCase() === "practice";
        // Practice -> test boundary: mirror the desktop task runner, which
        // automatically shows a "practice complete, the test will now begin"
        // gate at this point. Without it, online participants were dropped
        // straight from the last practice trial into the real test trials with
        // no break or heads-up (QA 2026-07-24, BUG-038). Fires once, at the
        // first non-practice block that follows a practice block.
        if (prevWasPractice && !isPractice) {
          timeline.push({
            type: htmlKeyboardResponse,
            stimulus: instructionHtml(
              "Practice complete.\n\nThe test will now begin.\n\n" +
                "When you are ready, press SPACE to start.",
            ),
            choices: [" "],
          });
        }
        if (block.instructions) {
          timeline.push({
            type: htmlKeyboardResponse,
            stimulus: instructionHtml(block.instructions),
            choices: [" "],
          });
        }
        for (const plan of block.trials) {
          const planPhases: Record<string, unknown>[] = [];
          for (const phase of definition.trial_template ?? []) {
            const t = phaseTrial(phase, plan);
            if (t) {
              planPhases.push(t);
              timeline.push(t);
            }
          }
          if (isPractice || !surveyTrigger || surveyN <= 0 || planPhases.length === 0) {
            continue;
          }
          surveyTrialCount += 1;
          if (surveyTrialCount % surveyN === 0) {
            // Pause the timeline (jsPsych pauses after the current trial finishes)
            // and overlay the survey; onTaskSurveySubmit resumes it.
            const last = planPhases[planPhases.length - 1];
            const prev = last.on_finish as
              | ((data: { response: string | null; rt: number | null }) => void)
              | undefined;
            last.on_finish = (data: { response: string | null; rt: number | null }) => {
              if (typeof prev === "function") prev(data);
              jsPsych.pauseExperiment();
              fireSurvey(surveyTrigger);
            };
          }
        }
        prevWasPractice = isPractice;
      }

      jsPsych.run(timeline as unknown as Parameters<typeof jsPsych.run>[0]);
    })();

    return () => {
      if (!armed) return; // ignore StrictMode's immediate, spurious cleanup
      cancelled = true;
      if (mrtCleanup) mrtCleanup();
      try {
        jsPsychInstance?.abortExperiment();
      } catch {
        /* already finished */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div ref={hostRef} className="jol-task" />
      {activeSurvey ? (
        <div className="jol-task-survey-overlay">
          <SurveyForm
            questions={activeSurvey.questions}
            submitLabel="Submit"
            onSubmit={onTaskSurveySubmit}
          />
        </div>
      ) : null}
    </>
  );
}
